import { Ad, AdCampaign, InsightsAds } from '../../types/marketing';
import BreakdownCard from './BreakdownCard';
import DataTable, { DataTableColumn } from './DataTable';

export const META_FOOTNOTE = 'Meta reports itself; numbers do not reconcile 1:1 with GA4 or our counts.';

const CAMPAIGN_COLUMNS: DataTableColumn<AdCampaign>[] = [
  { key: 'campaign', header: 'Campaign', accessor: (r) => r.name || r.campaignKey, format: 'text' },
  { key: 'objective', header: 'Objective', accessor: (r) => r.objective, format: 'text' },
  { key: 'spend', header: 'Spend (AED)', accessor: (r) => r.spend, format: 'aed' },
  { key: 'impressions', header: 'Impressions', accessor: (r) => r.impressions, format: 'int' },
  { key: 'reach', header: 'Reach', accessor: (r) => r.reach, format: 'int' },
  { key: 'frequency', header: 'Frequency', accessor: (r) => r.frequency, format: 'aed' },
  { key: 'linkClicks', header: 'Link clicks', accessor: (r) => r.linkClicks, format: 'int' },
  // Meta's CTR is a percentage (1.23 = 1.23%); the table takes ratios.
  { key: 'ctr', header: 'CTR', accessor: (r) => (r.ctr == null ? null : r.ctr / 100), format: 'pct' },
  { key: 'cpm', header: 'CPM (AED)', accessor: (r) => r.cpm, format: 'aed' },
  { key: 'landingPageViews', header: 'Landing page views', accessor: (r) => r.landingPageViews, format: 'int' },
  { key: 'results', header: 'Results', accessor: (r) => r.results, format: 'int' },
  { key: 'costPerResult', header: 'Cost per result (AED)', accessor: (r) => r.costPerResult, format: 'aed' },
];

const AD_COLUMNS: DataTableColumn<Ad>[] = [
  { key: 'ad', header: 'Ad', accessor: (r) => r.adName || r.adId, format: 'text' },
  { key: 'spend', header: 'Spend (AED)', accessor: (r) => r.spend, format: 'aed' },
  { key: 'linkClicks', header: 'Link clicks', accessor: (r) => r.linkClicks, format: 'int' },
  { key: 'results', header: 'Results', accessor: (r) => r.results, format: 'int' },
  { key: 'costPerResult', header: 'Cost per result (AED)', accessor: (r) => r.costPerResult, format: 'aed' },
];

const hourLabel = (h: string) => (/^\d{1,2}$/.test(h) ? `${h.padStart(2, '0')}:00` : h);

interface Props {
  data: InsightsAds;
  range: { from: string; to: string; timezone: string };
}

/** Spec 084 Ads tab — Meta campaigns (expand for their ads), and age × gender, region, hour-of-day breakdowns. */
export default function AdsSection({ data, range }: Props) {
  const adsOf = (key: string) => data.ads.filter((a) => a.campaignKey === key);
  const { ageGender, region, hour } = data.breakdowns;
  return (
    <div className="mkt-section" data-testid="section-ads">
      <section className="mkt-card">
        <DataTable caption="Meta campaigns" columns={CAMPAIGN_COLUMNS} rows={data.campaigns} rowKey={(r) => r.campaignKey}
          defaultSort={{ key: 'spend', dir: 'desc' }} emptyText="No Meta campaign spent in this range" exportMeta={range} testId="ads-campaigns"
          renderExpanded={(c) => (
            <div className="mkt-expanded" data-testid={`ads-expanded-${c.campaignKey}`}>
              {c.adsManagerUrl
                ? <a className="mkt-link" href={c.adsManagerUrl} target="_blank" rel="noopener noreferrer" data-testid="ads-manager-link">Open in Ads Manager<span className="sr-only"> (opens in a new tab)</span></a>
                : <span className="mkt-foot">No Meta campaign id yet, so there is no Ads Manager link.</span>}
              <DataTable caption={`Ads in ${c.name || c.campaignKey}`} columns={AD_COLUMNS} rows={adsOf(c.campaignKey)} rowKey={(a) => a.adId}
                defaultSort={{ key: 'spend', dir: 'desc' }} emptyText="No ad-level numbers for this campaign" exportMeta={range} />
            </div>
          )} />
      </section>
      <div className="mkt-breakdowns">
        <BreakdownCard title="Age × gender" testId="breakdown-age-gender"
          rows={ageGender.map((r) => ({ key: `${r.age}|${r.gender}`, label: `${r.age} · ${r.gender}`, spend: r.spend, clicks: r.clicks, results: r.results }))} />
        <BreakdownCard title="Region" testId="breakdown-region"
          rows={region.map((r) => ({ key: r.region, label: r.region, spend: r.spend, clicks: r.clicks, results: r.results }))} />
        <BreakdownCard title="Hour of day" testId="breakdown-hour" keepOrder limit={24}
          rows={hour.map((r) => ({ key: r.hour, label: hourLabel(r.hour), spend: r.spend, clicks: r.clicks, results: r.results }))} />
      </div>
      <p className="mkt-foot" data-testid="ads-footnote">{META_FOOTNOTE}{data.note ? ` ${data.note}` : ''}</p>
    </div>
  );
}
